/**
 * Field grouping and human labels for Analysis Setup.
 *
 * PURE, and deliberately so. Every label a founder reads about where a value
 * came from is decided here rather than in a component, because the same value
 * appears in the setup editor, in `Inputs used`, and in the investor's read-only
 * context — and three components inventing three wordings for `aggregate_only`
 * is how "source only" quietly becomes "verified" in one of them.
 *
 * The field KEYS are the server's. They come from the methodology registry, and
 * a key with no entry here still renders (humanised) rather than disappearing —
 * a missing label must never hide an input that the analysis actually used.
 */

import type { DraftCandidate, ProvenancedInput } from '@/lib/strategic-analysis/types';

export type FieldGroupId =
  | 'market_definition'
  | 'customers_economics'
  | 'route_to_market'
  | 'dependencies_regulation'
  | 'known_alternatives';

export const FIELD_GROUP_LABELS: Record<FieldGroupId, string> = {
  market_definition: 'Market definition',
  customers_economics: 'Customers & economics',
  route_to_market: 'Route to market',
  dependencies_regulation: 'Dependencies & regulation',
  known_alternatives: 'Known alternatives',
};

/** Group order is the order a founder is asked to think about the business. */
export const FIELD_GROUP_ORDER: FieldGroupId[] = [
  'market_definition',
  'customers_economics',
  'route_to_market',
  'dependencies_regulation',
  'known_alternatives',
];

const GROUP_BY_FIELD: Record<string, FieldGroupId> = {
  offering: 'market_definition',
  primaryUseCase: 'market_definition',
  industryCategory: 'market_definition',
  marketDefinition: 'market_definition',
  marketBoundary: 'market_definition',
  timeHorizon: 'market_definition',

  customerSegments: 'customers_economics',
  buyerTypes: 'customers_economics',
  customerUnit: 'customers_economics',
  businessModel: 'customers_economics',
  pricingModel: 'customers_economics',
  typicalContractValue: 'customers_economics',
  baseYear: 'customers_economics',
  currency: 'customers_economics',

  salesMotion: 'route_to_market',
  geographies: 'route_to_market',
  stage: 'route_to_market',

  criticalDependencies: 'dependencies_regulation',
  dataTechnologyDependencies: 'dependencies_regulation',
  regulatedActivities: 'dependencies_regulation',
  jurisdictions: 'dependencies_regulation',
  workforceModel: 'dependencies_regulation',
  physicalFootprint: 'dependencies_regulation',

  knownCompetitors: 'known_alternatives',
  knownSubstitutes: 'known_alternatives',
  differentiators: 'known_alternatives',
  switchingCostMechanisms: 'known_alternatives',
};

const FIELD_LABELS: Record<string, string> = {
  offering: 'Offering',
  primaryUseCase: 'Primary use case',
  industryCategory: 'Industry / category',
  marketDefinition: 'Market definition',
  marketBoundary: 'Market boundary',
  timeHorizon: 'Time horizon',
  customerSegments: 'Customer segments',
  buyerTypes: 'Buyer roles',
  customerUnit: 'Customer unit',
  businessModel: 'Business model',
  pricingModel: 'Pricing basis',
  typicalContractValue: 'Typical contract value',
  baseYear: 'Base year',
  currency: 'Currency',
  salesMotion: 'Sales motion',
  geographies: 'Geographies',
  stage: 'Stage',
  criticalDependencies: 'Critical dependencies',
  dataTechnologyDependencies: 'Data & technology dependencies',
  regulatedActivities: 'Regulated activities',
  jurisdictions: 'Jurisdictions',
  workforceModel: 'Workforce model',
  physicalFootprint: 'Physical footprint',
  knownCompetitors: 'Known competitors',
  knownSubstitutes: 'Known substitutes',
  differentiators: 'Differentiators',
  switchingCostMechanisms: 'Switching costs',
};

/** An unmapped key lands in Market definition rather than vanishing. */
export function groupForField(fieldKey: string): FieldGroupId {
  return GROUP_BY_FIELD[fieldKey] ?? 'market_definition';
}

/**
 * `someFieldKey` -> `Some field key`, so a new server key is still readable.
 * Sentence case, matching the curated labels above — a fallback that shouted in
 * Title Case would look like a different kind of thing than its neighbours.
 */
export function labelForField(fieldKey: string): string {
  const known = FIELD_LABELS[fieldKey];
  if (known) return known;
  // A callback replacer, not a `$1` backreference string — the latter reads
  // as a currency amount to scripts/ci/check-native-commerce.ts's PRICE rule
  // (R2), which scans literal source text and cannot tell a regex
  // replacement token from a dollar figure. Same output, no false positive.
  const spaced = fieldKey.replace(/[A-Z]/g, (c) => ` ${c}`).trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function groupFields(fieldKeys: readonly string[]): { id: FieldGroupId; fields: string[] }[] {
  return FIELD_GROUP_ORDER.map((id) => ({
    id,
    fields: fieldKeys.filter((k) => groupForField(k) === id).sort(),
  })).filter((g) => g.fields.length > 0);
}

// ---------------------------------------------------------------------------
// Provenance vocabulary
// ---------------------------------------------------------------------------

export const ORIGIN_LABELS: Record<ProvenancedInput['originLabel'], string> = {
  profile: 'Profile',
  onboarding: 'Onboarding',
  pitch_deck: 'Pitch deck',
  interview: 'Interview',
  data_room: 'Data room',
  founder: 'You',
};

/**
 * `aggregate_only` reads as "source only" rather than as a confidence level.
 *
 * The distinction it carries is narrow and exact: we know the value came from
 * that source, but the code cannot prove WHICH field it came from. Wording it as
 * a confidence ("medium") would invite it to be read as "probably right", which
 * is a different claim entirely and one nothing here supports.
 */
export const PRECISION_LABELS: Record<DraftCandidate['provenancePrecision'], string> = {
  field_exact: 'Field exact',
  aggregate_only: 'Source only',
};

export const PRECISION_EXPLANATIONS: Record<DraftCandidate['provenancePrecision'], string> = {
  field_exact: 'This exact field was recorded from this source.',
  aggregate_only:
    'This value came from this source, but the exact field it was taken from was not recorded.',
};

/**
 * Where the value stands, NOT how confident anyone is.
 *
 * `extracted` is the one that matters: an AI reading a deck produces a
 * candidate, never a fact, and a founder confirming it makes it the founder's
 * view — not an externally verified one.
 */
export const VERIFICATION_LABELS: Record<ProvenancedInput['verificationLabel'], string> = {
  founder_provided: 'Founder-provided',
  extracted: 'Extracted — needs your confirmation',
  externally_verified: 'Externally verified',
};

export const ASSERTION_LABELS: Record<DraftCandidate['assertionStatus'], string> = {
  observed: 'Observed',
  extracted: 'Extracted',
  asserted: 'Founder-provided',
};

export const VISIBILITY_LABELS: Record<ProvenancedInput['publicationScope'], string> = {
  founder_private: 'Private to your team',
  data_room_restricted: 'Data-room restricted',
  investor_publishable: 'Visible to permitted investors',
};

/**
 * Does publishing this value to investors require an explicit decision?
 *
 * A value that is already investor-publishable at its source needs no decision.
 * A private-derived one does, every time, and it is asked as a question rather
 * than defaulted — a default here would publish interview and data-room material
 * because nobody unticked a box.
 */
export function needsPublicationDecision(candidate: {
  visibility: ProvenancedInput['publicationScope'];
}): boolean {
  return candidate.visibility !== 'investor_publishable';
}

/** Human summary of a value, for chips and one-line rows. */
export function summarizeValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length > 0 ? value.map(String).join(', ') : '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export const WITHHELD_VALUE_LABEL = 'Provided, not shared with investors';

/**
 * Fields whose value is a LIST.
 *
 * Mirrors the fields read with `list()` in `buildProjection`
 * (supabase/functions/strategic-analysis/index.ts). That projection drops
 * anything that is not an array, so a founder answer sent as a comma-joined
 * SENTENCE for one of these is accepted by the save, stored on the context row,
 * and then silently never reaches research — the field reads as answered and
 * behaves as missing.
 */
export const LIST_FIELDS: ReadonlySet<string> = new Set([
  'customerSegments',
  'buyerTypes',
  'geographies',
  'jurisdictions',
  'knownCompetitors',
  'knownSubstitutes',
]);

/**
 * One founder answer, typed as free text, turned into the value the field holds.
 *
 * Returns undefined for a blank answer: an empty answer is not an empty value,
 * it is NO answer, and sending one would record the founder as having confirmed
 * a blank.
 */
export function parseFounderAnswer(fieldKey: string, text: string): unknown | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  if (!LIST_FIELDS.has(fieldKey)) return trimmed;

  const items = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
  return items.length > 0 ? items : undefined;
}

/**
 * What to render for one approved field.
 *
 * TWO DIFFERENT BLANKS. A field with no `value` KEY and a non-publishable scope
 * was answered and withheld — the context row is investor-readable, so the value
 * was never written to it. A field whose value is null or empty genuinely has
 * nothing behind it. Rendering both as the em-dash tells an investor the company
 * has no answer, when in one case it has one and chose not to share it.
 *
 * The scope is part of the test on purpose: the write-side guard enforces
 * "not publishable => no value", not the converse, so a publishable field that
 * somehow lacks a value is malformed rather than withheld, and reads as empty.
 */
export function describeInputValue(input: ProvenancedInput): string {
  const withheld = !('value' in input) && input.publicationScope !== 'investor_publishable';
  return withheld ? WITHHELD_VALUE_LABEL : summarizeValue(input.value);
}
